import { Log } from '../logger.js';
import { effect } from '../signals.js';
import { 
  cocktails,
  loading, 
  error,
  loadCocktails,
  showFavoritesOnly,
  searchTerm,
  updateFilteredCount
} from '../cocktail-state.js';
import { filterCocktails } from '../filterCocktails.js';
import './cocktail-card.js';

const _log = Log('cocktail-list.js');

class CocktailList extends HTMLElement {
  constructor() {
    super();
    this.currentCocktails = [];
    // Initialize collapse state from localStorage to fix refresh bug
    const savedCollapseState = localStorage.getItem('collapseContent');
    this.collapseContent = savedCollapseState === 'true';
    _log.blue('Initialized collapse state from localStorage:', savedCollapseState, '→', this.collapseContent);
  }

  connectedCallback() {
    _log.green('Component connected');
    this.renderStructure();
    this.setupReactiveEffects();
    this.setupCollapseListener();
    
    // Load cocktails on mount
    loadCocktails();
  }

  renderStructure() {
    this.innerHTML = `
      <div class="cocktails-grid" id="cocktails-grid">
        <div class="loading-state">
          <div class="loading-spinner"></div>
          <p>Loading cocktails...</p>
        </div>
      </div>
    `;
  }

  setupCollapseListener() {
    // Listen for collapse state changes from header
    document.addEventListener('collapse-changed', (e) => {
      this.collapseContent = e.detail.collapsed;
      // Use CSS-based updates instead of re-rendering for better performance
      this.updateCollapseStateForAllCards();
    });
  }

  setupReactiveEffects() {
    // React to cocktails changes - render all cards once, then use CSS for filtering
    effect(() => {
      this.currentCocktails = cocktails.value;
      this.renderCocktails(this.currentCocktails);
    });

    // React to loading state changes
    effect(() => {
      const isLoading = loading.value;
      this.renderLoadingState(isLoading);
    });

    // React to error state changes
    effect(() => {
      const errorMessage = error.value;
      if (errorMessage) {
        this.renderError(errorMessage);
      }
    });

    // React to search changes - use CSS filtering instead of re-rendering
    effect(() => {
      const searchQuery = searchTerm.value;
      this.updateSearchFilterCSS(searchQuery);
    });

    // React to favorites filter changes - use CSS instead of re-rendering
    effect(() => {
      const favoritesOnly = showFavoritesOnly.value;
      this.updateFavoritesFilterCSS(favoritesOnly);
    });
  }

  // Efficiently update collapse state for all existing cards without re-rendering
  updateCollapseStateForAllCards() {
    _log.blue('Updating collapse state for all cards via CSS:', this.collapseContent);
    
    const cardElements = this.querySelectorAll('cocktail-card');
    cardElements.forEach(cardElement => {
      // Update data attribute to trigger CSS-based collapse
      cardElement.dataset.collapsed = String(this.collapseContent);
    });
    
    _log.green(`Updated collapse state for ${cardElements.length} cards - no re-rendering needed!`);
  }

  // Efficiently update search filter using CSS instead of array filtering + re-rendering
  updateSearchFilterCSS(searchQuery) {
    _log.blue('Updating search filter via CSS:', searchQuery);
    
    const cardElements = this.querySelectorAll('cocktail-card');
    const trimmedQuery = searchQuery?.trim().toLowerCase() || '';
    let visibleCount = 0;
    
    cardElements.forEach(cardElement => {
      const cocktailData = cardElement.cocktailData;
      let isVisible = true;
      
      // Apply search filter logic (same as filterCocktails but CSS-based)
      if (trimmedQuery && cocktailData) {
        isVisible = false;
        
        // Check name
        if (cocktailData.name?.toLowerCase().includes(trimmedQuery)) {
          isVisible = true;
        }
        // Check category
        else if (cocktailData.category?.toLowerCase().includes(trimmedQuery)) {
          isVisible = true;
        }
        // Check ingredients
        else if (cocktailData.ingredients?.toLowerCase().includes(trimmedQuery)) {
          isVisible = true;
        }
      }
      
      // Update CSS data attribute to show/hide
      cardElement.dataset.filtered = String(!isVisible);
      
      if (isVisible) {
        visibleCount++;
      }
    });
    
    // Update the filtered count in the state so stats display correctly
    updateFilteredCount(this.getActualVisibleCount());
    
    _log.green(`Search filter updated - showing ${visibleCount}/${cardElements.length} cards via CSS - no re-rendering needed!`);
  }

  // Efficiently update favorites filter using CSS instead of array filtering
  updateFavoritesFilterCSS(showFavoritesOnly) {
    _log.blue('Updating favorites filter via CSS:', showFavoritesOnly);
    
    const gridContainer = this.querySelector('#cocktails-grid');
    if (gridContainer) {
      // Use data attribute to trigger CSS-based filtering
      gridContainer.dataset.showFavoritesOnly = String(showFavoritesOnly);
      
      const totalCards = this.querySelectorAll('cocktail-card').length;
      const visibleCards = showFavoritesOnly 
        ? this.querySelectorAll('cocktail-card .favorite-button.favorited').length
        : totalCards;
      
      // Update the filtered count in the state so stats display correctly
      updateFilteredCount(this.getActualVisibleCount());
      
      _log.green(`Favorites filter updated - showing ${visibleCards}/${totalCards} cards via CSS - no re-rendering needed!`);
    }
  }

  renderCocktails(cocktails) {
    const gridContainer = this.querySelector('#cocktails-grid');
    if (!gridContainer) return;

    if (loading.value) {
      return; // Loading handled by renderLoadingState
    }

    if (error.value) {
      return; // Error handled by renderError
    }

    // Validate cocktails array
    if (!Array.isArray(cocktails)) {
      _log.red('Invalid cocktails data - expected array, got:', typeof cocktails);
      this.renderError('Invalid data format received');
      return;
    }

    if (!cocktails.length) {
      // Clear existing content
      gridContainer.replaceChildren();
      
      // Create empty state container
      const emptyDiv = document.createElement('div');
      emptyDiv.className = 'empty-state';
      
      // Create title
      const title = document.createElement('h3');
      title.textContent = 'No cocktails found';
      
      // Create message
      const message = document.createElement('p');
      message.textContent = 'Try adjusting your search terms';
      
      // Assemble empty state
      emptyDiv.appendChild(title);
      emptyDiv.appendChild(message);
      
      gridContainer.appendChild(emptyDiv);
      return;
    }

    // Clear existing content
    gridContainer.innerHTML = '';

    // Create cocktail-card elements with validation
    _log.blue(`Rendering ${cocktails.length} cocktails with collapse state:`, this.collapseContent);
    cocktails.forEach((cocktail, index) => {
      const cardElement = document.createElement('cocktail-card');
      
      // The cocktail-card component will handle its own validation
      // We just need to pass the data and let it validate
      cardElement.cocktailData = cocktail;
      
      // Validate index as well
      if (typeof index === 'number' && index >= 0) {
        cardElement.index = index;
      }
      
      // Use data attribute for CSS-based collapse (more efficient than re-rendering)
      cardElement.dataset.collapsed = String(this.collapseContent);
      gridContainer.appendChild(cardElement);
    });
    
    // After all cards are rendered, ensure collapse state is applied
    // This fixes the localStorage state not being applied on page reload
    setTimeout(() => {
      this.updateCollapseStateForAllCards();
      // Also update the filtered count for accurate stats
      updateFilteredCount(this.getActualVisibleCount());
    }, 0);
  }
  
  // Helper method to get the actual visible count based on current filters
  getActualVisibleCount() {
    const cardElements = this.querySelectorAll('cocktail-card');
    let visibleCount = 0;
    
    // Check both search filtering and favorites filtering
    const currentSearch = searchTerm.value?.trim().toLowerCase() || '';
    const showingFavoritesOnly = showFavoritesOnly.value;
    
    cardElements.forEach(cardElement => {
      let isVisible = true;
      
      // Check search filter
      if (currentSearch && cardElement.cocktailData) {
        const cocktailData = cardElement.cocktailData;
        const matchesSearch = 
          cocktailData.name?.toLowerCase().includes(currentSearch) ||
          cocktailData.category?.toLowerCase().includes(currentSearch) ||
          cocktailData.ingredients?.toLowerCase().includes(currentSearch);
        
        if (!matchesSearch) {
          isVisible = false;
        }
      }
      
      // Check favorites filter
      if (showingFavoritesOnly && isVisible) {
        const favoriteButton = cardElement.querySelector('.favorite-button');
        const isFavorited = favoriteButton?.classList.contains('favorited');
        
        if (!isFavorited) {
          isVisible = false;
        }
      }
      
      if (isVisible) {
        visibleCount++;
      }
    });
    
    return visibleCount;
  }


  renderLoadingState(isLoading) {
    const gridContainer = this.querySelector('#cocktails-grid');
    if (!gridContainer) return;

    if (isLoading) {
      gridContainer.innerHTML = `
        <div class="loading-state">
          <div class="loading-spinner"></div>
          <p>Loading cocktails...</p>
        </div>
      `;
    }
  }

  renderError(errorMessage) {
    const gridContainer = this.querySelector('#cocktails-grid');
    if (gridContainer) {
      // Clear existing content
      gridContainer.replaceChildren();
      
      // Create error state container
      const errorDiv = document.createElement('div');
      errorDiv.className = 'error-state';
      
      // Create title
      const title = document.createElement('h3');
      title.textContent = 'Error loading cocktails';
      
      // Create error message paragraph (secure text content)
      const message = document.createElement('p');
      message.textContent = errorMessage; // Automatically escapes HTML
      
      // Create retry button with proper event listener
      const retryButton = document.createElement('button');
      retryButton.textContent = 'Retry';
      retryButton.addEventListener('click', () => {
        window.location.reload();
      });
      
      // Assemble error state
      errorDiv.appendChild(title);
      errorDiv.appendChild(message);
      errorDiv.appendChild(retryButton);
      
      gridContainer.appendChild(errorDiv);
    }
  }
}

customElements.define('cocktail-list', CocktailList);
